import { Link } from "react-router-dom";
import { useCategories, useProducts } from "../features/catalog/api";
import ProductCard from "../features/catalog/ProductCard";
import CategoryCard from "../features/catalog/CategoryCard";
import SCSpinner from "../components/loaders/SCSpinner";
import ErrorState from "../components/ui/ErrorState";
import { useSettings } from "../features/settings/api";
import useSEO from "../hooks/useSEO";

export default function Home() {
  useSEO({});
  const { data: categories, isLoading: loadingCategories, isError: categoriesError, refetch: refetchCategories } = useCategories();
  const { data: featured, isLoading: loadingFeatured, isError: featuredError, refetch: refetchFeatured } = useProducts({ featured: true });
  const { data: settings } = useSettings();
  const { heroHeadline = "", heroAccent = "", heroSubtext = "", heroCtaLabel = "" } = settings ?? {};

  return (
    <div>
      <section
        className="flex flex-col items-center justify-center px-6 py-16 text-center"
        style={{
          background:
            "radial-gradient(ellipse at 50% 0%, rgba(184,134,11,0.06), rgba(255,255,255,0) 60%)",
        }}
      >
        <h1 className="font-serif text-4xl font-normal text-ink sm:text-5xl">
          {heroHeadline}
          <br />
          <span className="bg-gradient-to-br from-gold-300 via-gold-500 to-gold-600 bg-clip-text text-transparent">
            {heroAccent}
          </span>
        </h1>
        <p className="mt-5 max-w-md text-ink-light">{heroSubtext}</p>
        <Link
          to="/categories"
          className="mt-8 inline-block rounded-2xl bg-[#2b2b2d] px-9 py-3.5 text-xs tracking-[2px] text-white shadow-floating dark:bg-gold-500 dark:text-ink-dark dark:shadow-gold"
        >
          {heroCtaLabel}
        </Link>
      </section>

      <section className="px-4 py-8">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-serif text-xl text-ink">Shop by Category</h2>
          <Link to="/categories" className="text-xs tracking-wide text-gold-600 dark:text-gold-400">
            View all
          </Link>
        </div>
        {loadingCategories ? (
          <div className="flex justify-center py-10">
            <SCSpinner size={48} />
          </div>
        ) : categoriesError ? (
          <ErrorState message="Couldn't load categories." onRetry={refetchCategories} />
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {(categories ?? []).slice(0, 4).map((c) => (
              <CategoryCard key={c.id} category={c} />
            ))}
          </div>
        )}
      </section>

      <section className="px-4 py-8">
        <h2 className="mb-4 font-serif text-xl text-ink">Featured</h2>
        {loadingFeatured ? (
          <div className="flex justify-center py-10">
            <SCSpinner size={48} />
          </div>
        ) : featuredError ? (
          <ErrorState message="Couldn't load featured products." onRetry={refetchFeatured} />
        ) : (
          <div className="grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 lg:grid-cols-4">
            {(featured ?? []).map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
